import { Activity } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useLatestEquipmentReadings } from '@/features/counter-readings/queries';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import { useEquipment } from '@/features/equipment/queries';
import type { CounterFieldConfig } from '@/features/equipment-types/types';
import type { WorkOrder } from '@/features/work-orders/types';
import { RecordCounterModal } from '../equipment/RecordCounterModal';

interface WorkOrderCounterActionProps {
  workOrder: WorkOrder;
}

/**
 * Header-bar action that surfaces the L4.5 "record counter reading"
 * flow inline on a WO. Only renders when the linked equipment's
 * type declares at least one counter field — keeps the toolbar
 * clean for WOs on equipment without counters (e.g. hull anodes).
 *
 * If multiple counters are declared, the user picks which one they
 * want to record via the field-specific button.
 */
export function WorkOrderCounterAction({ workOrder }: WorkOrderCounterActionProps) {
  const { t } = useTranslation();
  const { data: vesselEquipment } = useEquipment({ vesselId: workOrder.vesselId });
  const { data: types } = useEquipmentTypes();
  const equipment = vesselEquipment?.find((e) => e.id === workOrder.equipmentId);
  const equipmentType = types?.find((tp) => tp.id === equipment?.equipmentTypeId);
  const fields: CounterFieldConfig[] = useMemo(
    () => equipmentType?.counterFields ?? [],
    [equipmentType],
  );
  const { data: latestMap = {} } = useLatestEquipmentReadings(equipment?.id);
  const [openField, setOpenField] = useState<CounterFieldConfig | null>(null);

  if (!equipment || fields.length === 0) return null;

  return (
    <>
      {fields.map((cfg) => (
        <Button
          key={cfg.field}
          variant="outline"
          size="sm"
          onClick={() => setOpenField(cfg)}
        >
          <Activity aria-hidden className="h-3.5 w-3.5" />
          {t('counter.workOrder.recordAction')}
          <span className="ml-1 text-xs text-muted-foreground">
            ({t(`counter.field.${cfg.field}`, { defaultValue: cfg.label })})
          </span>
        </Button>
      ))}
      {openField && (
        <RecordCounterModal
          open
          onOpenChange={(o) => !o && setOpenField(null)}
          equipmentId={equipment.id}
          field={openField}
          lastReading={latestMap[openField.field] ?? null}
          workOrderId={workOrder.id}
        />
      )}
    </>
  );
}
